// NPC Window Viewer for Development Testing
// Allows developers to load and test any NPC window in the game

import React, { useState, useEffect } from 'react';
import { NPCData, getAllNPCs } from '../../../shared/NPCData';
import { ITEM_DATABASE } from '../../../shared/ItemData';
import { questSystem } from '../../systems/quest/QuestSystem';
import { Panel } from '../primitives/Panel';
import { Card } from '../primitives/Card';
import { Button } from '../primitives/Button';
import { TabBar } from '../primitives/TabBar';
import {
  COLORS,
  SPACE,
  RADII,
  FONT_SIZE,
  FONTS,
} from '../theme';
import { gameLogger } from '../../../utils/GameLogger';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { EventBus, EVENTS } from '../../../shared/EventBus';

const sectionTitleStyle: React.CSSProperties = {
  color: COLORS.textAccent,
  marginBottom: SPACE.md,
};

const fieldLabelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: SPACE.sm,
  color: COLORS.textSecondary,
};

const fieldInputStyle: React.CSSProperties = {
  width: '100%',
  padding: SPACE.md,
  background: COLORS.surfaceSunken,
  border: `1px solid ${COLORS.border}`,
  borderRadius: RADII.md,
  color: COLORS.textPrimary,
  fontFamily: FONTS.ui,
  fontSize: FONT_SIZE.md,
};

const calloutStyle = (accent: string): React.CSSProperties => ({
  background: COLORS.surfaceSunken,
  border: `1px solid ${accent}`,
  borderRadius: RADII.lg,
  padding: SPACE.xl,
  marginTop: SPACE.xxl,
});

/**
 * NPCWindowViewerContent — propless body of the NPC Window Viewer dev tool
 * (Phase 2 fold). WindowHost mounts it inside the shared ToolWindow chrome (only
 * while the `npcWindowViewer` window is open — opened via
 * windowBridge.toggle('npcWindowViewer')). The old centered fixed-div panel +
 * title + Close button become ToolWindow's chrome. Player-free (NPCData /
 * ITEM_DATABASE / questSystem singletons).
 */
export const NPCWindowViewerContent: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'browse' | 'test' | 'edit'>('browse');
  const [selectedNPC, setSelectedNPC] = useState<NPCData | null>(null);
  const [allNPCs, setAllNPCs] = useState<NPCData[]>([]);
  const [testWindowType, setTestWindowType] = useState<'shop' | 'quest' | 'dialogue' | null>(null);
  const [testData, setTestData] = useState<any>(null);

  // Load all NPCs on mount (the host mounts this body only while the window is open).
  useEffect(() => {
    setAllNPCs(getAllNPCs());
  }, []);

  // Load test data when NPC is selected
  useEffect(() => {
    if (selectedNPC) {
      loadTestData(selectedNPC);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNPC]);

  const loadTestData = (npc: NPCData) => {
    const data: any = {
      npc,
      timestamp: new Date().toISOString()
    };

    // Load quest data if NPC has quests
    if (npc.availableQuests.length > 0) {
      const quests = npc.availableQuests.map(questId => questSystem.getQuest(questId)).filter(Boolean);
      data.quests = quests;
    }

    // Load shop items if NPC is a merchant
    if (npc.role === 'shop' && npc.shopItems) {
      const items = npc.shopItems.map(itemId => {
        // Try to find item in ItemData
        const itemData = ITEM_DATABASE?.[itemId];
        return itemData || { id: itemId, name: `Unknown Item: ${itemId}` };
      });
      data.shopItems = items;
    }

    setTestData(data);
  };

  const handleNPCClick = (npc: NPCData) => {
    setSelectedNPC(npc);
    setActiveTab('test');
  };

  const handleTestWindow = (windowType: 'shop' | 'quest' | 'dialogue') => {
    setTestWindowType(windowType);

    // Emit appropriate event for the window type
    if (selectedNPC) {
      const event = new CustomEvent(`dev:${windowType}Opened`, {
        detail: { npc: selectedNPC, data: testData }
      });
      window.dispatchEvent(event);
      gameLogger.info('ui', `[NPCWindowViewer] Emitted dev:${windowType}Opened event for ${selectedNPC.name}`);
    }
  };

  const handleCloseTestWindow = () => {
    setTestWindowType(null);
  };

  const handleTeleportToNPC = (npc: NPCData) => {
    // Move the local render immediately, then use the normal player-action
    // bridge so the server and remote clients receive the same teleport.
    const entitySystem = ServiceRegistry.getInstance().get('EntitySystem', { optional: true });
    entitySystem?.getPlayerManager()?.teleportPlayer(npc.x, npc.y);
    EventBus.getInstance().emit(EVENTS.PLAYER_ACTION_INTENT, {
      type: 'teleport',
      payload: { x: npc.x, y: npc.y },
    });

    // Keep the legacy event for development extensions that observe it.
    const event = new CustomEvent('dev:teleportToNPC', {
      detail: { x: npc.x, y: npc.y, npcId: npc.id }
    });
    window.dispatchEvent(event);
    gameLogger.info('ui', `[NPCWindowViewer] Requesting teleport to ${npc.name} at (${npc.x}, ${npc.y})`);
  };

  const handleSaveEdit = () => {
    if (selectedNPC) {
      gameLogger.info('ui', `[NPCWindowViewer] Would save NPC edits for ${selectedNPC.name}`);
      // In a real implementation, this would update the NPC database
      alert('NPC editing would be saved to database in production');
    }
  };

  return (
    <div style={{ fontFamily: FONTS.ui, height: '100%', overflowY: 'auto' }}>
      <div style={{ marginBottom: SPACE.xxl }}>
        <TabBar
          tabs={[
            { id: 'browse', label: 'Browse NPCs' },
            { id: 'test', label: 'Test Windows', disabled: !selectedNPC },
            { id: 'edit', label: 'Edit NPC', disabled: !selectedNPC }
          ]}
          activeTab={activeTab}
          onTabChange={(id) => setActiveTab(id as any)}
        />
      </div>

      {activeTab === 'browse' && (
        <div>
          <div style={{ marginBottom: SPACE.xxl }}>
            <h3 style={sectionTitleStyle}>Available NPCs ({allNPCs.length})</h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: SPACE.xl,
              maxHeight: '400px',
              overflowY: 'auto',
              padding: SPACE.md
            }}>
              {allNPCs.map(npc => (
                <Card
                  key={npc.id}
                  title={npc.name}
                  subtitle={`${npc.role} • (${npc.x}, ${npc.y})`}
                  onClick={() => handleNPCClick(npc)}
                >
                  <div style={{ fontSize: FONT_SIZE.md, color: COLORS.textSecondary }}>
                    <div><strong>ID:</strong> {npc.id}</div>
                    <div><strong>Sprite:</strong> {npc.sprite}</div>
                    <div><strong>Quests:</strong> {npc.availableQuests.length}</div>
                    {npc.role === 'shop' && (
                      <div><strong>Shop Items:</strong> {npc.shopItems?.length || 0}</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: SPACE.md }}>
                    <Button size="sm" onClick={(e) => {
                      e.stopPropagation();
                      handleNPCClick(npc);
                    }}>
                      Test
                    </Button>
                    <Button size="sm" variant="secondary" onClick={(e) => {
                      e.stopPropagation();
                      handleTeleportToNPC(npc);
                    }}>
                      Teleport
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          <div style={calloutStyle(COLORS.info)}>
            <strong>💡 Usage:</strong>
            <ul style={{ margin: `${SPACE.md}px 0`, paddingLeft: SPACE.xxl }}>
              <li>Click an NPC card to select it for testing</li>
              <li>Use "Test" to open NPC windows without traveling</li>
              <li>Use "Teleport" to move player to NPC location</li>
              <li>Switch to "Test Windows" tab after selecting an NPC</li>
            </ul>
          </div>
        </div>
      )}

      {activeTab === 'test' && selectedNPC && (
        <div>
          <div style={{ marginBottom: SPACE.xxl }}>
            <h3 style={{ color: COLORS.success, marginBottom: SPACE.md }}>
              Testing: {selectedNPC.name} ({selectedNPC.role})
            </h3>
            <div style={{
              background: COLORS.surfaceSunken,
              border: `1px solid ${COLORS.success}`,
              borderRadius: RADII.lg,
              padding: SPACE.xl,
              marginBottom: SPACE.xxl
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACE.md, fontSize: FONT_SIZE.lg }}>
                <div><strong>Position:</strong> ({selectedNPC.x}, {selectedNPC.y})</div>
                <div><strong>Sprite:</strong> {selectedNPC.sprite}</div>
                <div><strong>Available Quests:</strong> {selectedNPC.availableQuests.length}</div>
                <div><strong>Shop Items:</strong> {selectedNPC.shopItems?.length || 0}</div>
              </div>
            </div>

            <div style={{ marginBottom: SPACE.xxl }}>
              <h4 style={sectionTitleStyle}>Dialogue Preview</h4>
              <Panel variant="raised">
                <div style={{ fontStyle: 'italic', color: COLORS.textSecondary }}>"{selectedNPC.dialogue.greeting}"</div>
              </Panel>
            </div>

            <div style={{ marginBottom: SPACE.xxl }}>
              <h4 style={sectionTitleStyle}>Test Windows</h4>
              <div style={{ display: 'flex', gap: SPACE.xl, flexWrap: 'wrap' }}>
                {selectedNPC.role === 'shop' && (
                  <Button
                    variant="primary"
                    onClick={() => handleTestWindow('shop')}
                    disabled={!selectedNPC.shopItems || selectedNPC.shopItems.length === 0}
                  >
                    🛒 Test Shop Window
                  </Button>
                )}

                {selectedNPC.availableQuests.length > 0 && (
                  <Button
                    variant="success"
                    onClick={() => handleTestWindow('quest')}
                  >
                    📜 Test Quest Offer
                  </Button>
                )}

                <Button
                  variant="secondary"
                  onClick={() => handleTestWindow('dialogue')}
                >
                  💬 Test Dialogue
                </Button>

                <Button
                  variant="secondary"
                  onClick={() => handleTeleportToNPC(selectedNPC)}
                >
                  🚀 Teleport to NPC
                </Button>
              </div>
            </div>

            {testData && (
              <div style={{ marginBottom: SPACE.xxl }}>
                <h4 style={sectionTitleStyle}>Loaded Data</h4>
                <div style={{
                  background: COLORS.surfaceSunken,
                  border: `1px solid ${COLORS.textAccent}`,
                  borderRadius: RADII.lg,
                  padding: SPACE.xl,
                  fontSize: FONT_SIZE.md,
                  maxHeight: '150px',
                  overflowY: 'auto'
                }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: FONTS.mono }}>
                    {JSON.stringify(testData, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {testWindowType && (
              <div style={calloutStyle(COLORS.brand)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong style={{ color: COLORS.brand }}>
                    Testing {testWindowType} window for {selectedNPC.name}
                  </strong>
                  <Button size="sm" variant="danger" onClick={handleCloseTestWindow}>
                    Close Test
                  </Button>
                </div>
                <div style={{ marginTop: SPACE.md, fontSize: FONT_SIZE.lg, color: COLORS.textSecondary }}>
                  Event <code>dev:{testWindowType}Opened</code> has been emitted.
                  {testWindowType === 'shop' && ' Check for shop UI component.'}
                  {testWindowType === 'quest' && ' Check for quest offer UI component.'}
                  {testWindowType === 'dialogue' && ' NPC dialogue should appear in game.'}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'edit' && selectedNPC && (
        <div>
          <div style={{ marginBottom: SPACE.xxl }}>
            <h3 style={{ color: COLORS.brand, marginBottom: SPACE.md }}>
              Editing: {selectedNPC.name}
            </h3>
            <div style={{ color: COLORS.danger, marginBottom: SPACE.xl }}>
              ⚠️ Note: NPC editing is read-only in this demo. In production, changes would be saved to the database.
            </div>

            <Panel variant="raised">
              <div style={{ display: 'grid', gap: SPACE.xl }}>
                <div>
                  <label style={fieldLabelStyle}>NPC Name</label>
                  <input
                    type="text"
                    value={selectedNPC.name}
                    onChange={(e) => setSelectedNPC({...selectedNPC, name: e.target.value})}
                    style={fieldInputStyle}
                  />
                </div>

                <div>
                  <label style={fieldLabelStyle}>Role</label>
                  <select
                    value={selectedNPC.role}
                    onChange={(e) => setSelectedNPC({...selectedNPC, role: e.target.value as any})}
                    style={fieldInputStyle}
                  >
                    <option value="quest_giver">Quest Giver</option>
                    <option value="shop">Shop</option>
                    <option value="trainer">Trainer</option>
                    <option value="guide">Guide</option>
                  </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACE.xl }}>
                  <div>
                    <label style={fieldLabelStyle}>X Position</label>
                    <input
                      type="number"
                      value={selectedNPC.x}
                      onChange={(e) => setSelectedNPC({...selectedNPC, x: parseInt(e.target.value) || 0})}
                      style={fieldInputStyle}
                    />
                  </div>
                  <div>
                    <label style={fieldLabelStyle}>Y Position</label>
                    <input
                      type="number"
                      value={selectedNPC.y}
                      onChange={(e) => setSelectedNPC({...selectedNPC, y: parseInt(e.target.value) || 0})}
                      style={fieldInputStyle}
                    />
                  </div>
                </div>

                <div>
                  <label style={fieldLabelStyle}>Greeting Dialogue</label>
                  <textarea
                    value={selectedNPC.dialogue.greeting}
                    onChange={(e) => setSelectedNPC({
                      ...selectedNPC,
                      dialogue: {...selectedNPC.dialogue, greeting: e.target.value}
                    })}
                    rows={3}
                    style={{ ...fieldInputStyle, resize: 'vertical' }}
                  />
                </div>
              </div>
            </Panel>

            <div style={{ display: 'flex', gap: SPACE.xl, marginTop: SPACE.xxl }}>
              <Button onClick={handleSaveEdit}>
                Save Changes
              </Button>
              <Button variant="secondary" onClick={() => setActiveTab('test')}>
                Back to Testing
              </Button>
            </div>
          </div>
        </div>
      )}


      <div style={{
        ...calloutStyle(COLORS.info),
        fontSize: FONT_SIZE.md,
        color: COLORS.textMuted
      }}>
        <strong>🔧 Developer Notes:</strong>
        <ul style={{ margin: `${SPACE.md}px 0`, paddingLeft: SPACE.xxl }}>
          <li>This tool emits custom events prefixed with <code>dev:</code></li>
          <li>Listen for these events in your UI components to show windows</li>
          <li>Use teleport to test NPC positioning and interactions</li>
          <li>All data is read-only in this demo version</li>
          <li>Animation Visualizer tracks all animated entities in real-time</li>
        </ul>
      </div>
    </div>
  );
};
